import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus,
    Search,
    Star,
    FolderOpen,
    Loader2,
    AlertCircle,
    FileText
} from 'lucide-react';
import {
    claudeQueryService,
    SavedReportDTO
} from '../../../../../services/claudeQueryService';
import { ReportCard } from './ReportCard';
import { ReportViewer } from './ReportViewer';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface ReportsDashboardProps {
    onEditReport: (report: SavedReportDTO) => void;
    onCreateReport: () => void;
}

export const ReportsDashboard: React.FC<ReportsDashboardProps> = ({
    onEditReport,
    onCreateReport
}) => {
    const { t } = useTranslation();
    const [reports, setReports] = useState<SavedReportDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState<string | null>(null);
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
    const [runningReports, setRunningReports] = useState<Set<number>>(new Set());
    const [openReport, setOpenReport] = useState<SavedReportDTO | null>(null);

    const loadReports = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await claudeQueryService.getReports();
            setReports(data);
        } catch (err: any) {
            setError(err.message || t('manager.modules.claudeQuery.reports.loadError', {}, 'Kunne ikke laste rapporter'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadReports();
    }, [loadReports]);

    const handleRunReport = async (id: number) => {
        try {
            setRunningReports(prev => new Set(prev).add(id));
            const execution = await claudeQueryService.executeReport(id);

            // Oppdater rapport-kortet med ny kjøringsinfo
            setReports(prev => prev.map(r => {
                if (r.reportId === id) {
                    return {
                        ...r,
                        lastRunAt: execution.startedAt,
                        lastRunStatus: execution.status,
                        lastRunRowCount: execution.rowCount ?? r.lastRunRowCount
                    };
                }
                return r;
            }));
        } catch (err: any) {
            setError(t('manager.modules.claudeQuery.reports.runError', { '0': err.message }, `Feil ved kjøring av rapport: ${err.message}`));
        } finally {
            setRunningReports(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    const handleDeleteReport = async (id: number) => {
        if (!window.confirm(t('manager.modules.claudeQuery.reports.deleteConfirm', {}, 'Er du sikker på at du vil slette denne rapporten?'))) return;

        try {
            await claudeQueryService.deleteReport(id);
            setReports(prev => prev.filter(r => r.reportId !== id));
        } catch (err: any) {
            setError(t('manager.modules.claudeQuery.reports.deleteError', { '0': err.message }, `Feil ved sletting: ${err.message}`));
        }
    };

    const handleToggleFavorite = async (id: number, isFavorite: boolean) => {
        try {
            await claudeQueryService.updateReport(id, { isFavorite });
            setReports(prev => prev.map(r =>
                r.reportId === id ? { ...r, isFavorite } : r
            ));
        } catch (err: any) {
            setError(t('manager.modules.claudeQuery.reports.updateError', { '0': err.message }, `Feil ved oppdatering: ${err.message}`));
        }
    };

    // Kategorier uavhengig av store/små bokstaver («Plukk» og «PLukk» er samme kategori).
    // Visningsnavnet er første variant med stor forbokstav, ellers første variant.
    const categoryKey = (c?: string | null) => (c ?? '').trim().toLocaleLowerCase('nb-NO');
    const categoryNames = new Map<string, string>();
    reports.forEach(r => {
        const key = categoryKey(r.category);
        if (!key) return;
        const name = r.category!.trim();
        const current = categoryNames.get(key);
        const looksNice = (n: string) => n[0] === n[0].toLocaleUpperCase('nb-NO') && n.slice(1) === n.slice(1).toLocaleLowerCase('nb-NO');
        if (!current || (!looksNice(current) && looksNice(name))) categoryNames.set(key, name);
    });
    const categories = Array.from(categoryNames.keys()).sort((a, b) => a.localeCompare(b, 'nb-NO'));

    // Filter reports
    const filteredReports = reports.filter(r => {
        if (showFavoritesOnly && !r.isFavorite) return false;
        if (filterCategory && categoryKey(r.category) !== filterCategory) return false;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return (
                r.name.toLowerCase().includes(q) ||
                r.description?.toLowerCase().includes(q) ||
                r.category?.toLowerCase().includes(q)
            );
        }
        return true;
    });

    // Group by category
    const groupedReports: Record<string, SavedReportDTO[]> = {};
    const uncategorized: SavedReportDTO[] = [];

    filteredReports.forEach(r => {
        const key = categoryKey(r.category);
        if (key) {
            const name = categoryNames.get(key) ?? r.category!;
            if (!groupedReports[name]) groupedReports[name] = [];
            groupedReports[name].push(r);
        } else {
            uncategorized.push(r);
        }
    });

    // Sort favorites first within each group
    const sortReports = (arr: SavedReportDTO[]) =>
        [...arr].sort((a, b) => {
            if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
            return a.sortOrder - b.sortOrder;
        });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="animate-spin text-blue-500" size={32} />
                <span className="ml-3 text-gray-500">{t('manager.modules.claudeQuery.reports.loading', {}, 'Laster rapporter...')}</span>
            </div>
        );
    }

    if (openReport) {
        return <ReportViewer report={openReport} onBack={() => setOpenReport(null)} onChanged={loadReports} />;
    }

    return (
        <div className="h-full flex flex-col">
            {/* Toolbar */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder={t('manager.modules.claudeQuery.reports.searchPlaceholder', {}, 'Søk i rapporter...')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm
                                 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                    />
                </div>

                {/* Category filter */}
                {categories.length > 0 && (
                    <select
                        value={filterCategory || ''}
                        onChange={(e) => setFilterCategory(e.target.value || null)}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white
                                 focus:outline-none focus:border-blue-400"
                    >
                        <option value="">{t('manager.modules.claudeQuery.reports.allCategories', {}, 'Alle kategorier')}</option>
                        {categories.map(key => (
                            <option key={key} value={key}>{categoryNames.get(key)}</option>
                        ))}
                    </select>
                )}

                {/* Favorites toggle */}
                <button
                    onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                    className={`flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm transition-colors ${
                        showFavoritesOnly
                            ? 'bg-yellow-50 border-yellow-300 text-yellow-700'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                >
                    <Star size={14} className={showFavoritesOnly ? 'fill-yellow-400' : ''} />
                    {t('manager.modules.claudeQuery.reports.favorites', {}, 'Favoritter')}
                </button>

                {/* Create */}
                <button
                    onClick={onCreateReport}
                    className="flex items-center gap-1.5 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm
                             hover:bg-blue-600 font-medium transition-colors"
                >
                    <Plus size={14} />
                    {t('manager.modules.claudeQuery.reports.newReport', {}, 'Ny rapport')}
                </button>
            </div>

            {/* Error */}
            {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4 text-sm text-red-700">
                    <AlertCircle size={16} />
                    {error}
                    <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
                        &times;
                    </button>
                </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-auto">
                {filteredReports.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                        {reports.length === 0 ? (
                            <>
                                <FileText size={48} className="mb-3 text-gray-300" />
                                <p className="text-lg font-medium text-gray-500">{t('manager.modules.claudeQuery.reports.emptyTitle', {}, 'Ingen rapporter ennå')}</p>
                                <p className="text-sm mt-1">
                                    {t('manager.modules.claudeQuery.reports.emptyDescription', {}, 'Lag din første rapport ved å stille et spørsmål i chatten og lagre resultatet.')}
                                </p>
                                <button
                                    onClick={onCreateReport}
                                    className="mt-4 flex items-center gap-1.5 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
                                >
                                    <Plus size={14} />
                                    {t('manager.modules.claudeQuery.reports.createReport', {}, 'Opprett rapport')}
                                </button>
                            </>
                        ) : (
                            <>
                                <FolderOpen size={48} className="mb-3 text-gray-300" />
                                <p className="text-lg font-medium text-gray-500">{t('manager.modules.claudeQuery.reports.emptyFilterTitle', {}, 'Ingen rapporter matcher filteret')}</p>
                                <p className="text-sm mt-1">{t('manager.modules.claudeQuery.reports.emptyFilterHint', {}, 'Prøv å endre søkeord eller filter.')}</p>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Render by category group */}
                        {Object.entries(groupedReports).sort(([a], [b]) => a.localeCompare(b)).map(([category, catReports]) => (
                            <div key={category}>
                                <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <FolderOpen size={14} />
                                    {category}
                                    <span className="text-gray-400 font-normal">({catReports.length})</span>
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {sortReports(catReports).map(report => (
                                        <ReportCard
                                            key={report.reportId}
                                            report={report}
                                            onRun={handleRunReport}
                                            onEdit={onEditReport}
                                            onDelete={handleDeleteReport}
                                            onToggleFavorite={handleToggleFavorite}
                                            isRunning={runningReports.has(report.reportId)}
                                            onOpen={setOpenReport}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Uncategorized */}
                        {uncategorized.length > 0 && (
                            <div>
                                {Object.keys(groupedReports).length > 0 && (
                                    <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <FileText size={14} />
                                        {t('manager.modules.claudeQuery.reports.uncategorized', {}, 'Ukategorisert')}
                                        <span className="text-gray-400 font-normal">({uncategorized.length})</span>
                                    </h2>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {sortReports(uncategorized).map(report => (
                                        <ReportCard
                                            key={report.reportId}
                                            report={report}
                                            onRun={handleRunReport}
                                            onEdit={onEditReport}
                                            onDelete={handleDeleteReport}
                                            onToggleFavorite={handleToggleFavorite}
                                            isRunning={runningReports.has(report.reportId)}
                                            onOpen={setOpenReport}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
